import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail, BACKEND_URL } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";

const resolveUrl = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);
const EMPTY = { title: "", body: "", image_url: "" };

/** Center stories/news — mini journal shown on the public center page. */
export const CenterPostsPanel = () => {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/center/posts");
      setPosts(data);
    } catch (_e) {
      /* non-blocking */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleImageUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, image_url: data.url }));
      toast.success("Image ready");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { title: form.title.trim(), body: form.body.trim(), image_url: form.image_url || null };
      if (editingId) {
        await api.put(`/center/posts/${editingId}`, payload);
        toast.success("Story updated");
      } else {
        await api.post("/center/posts", payload);
        toast.success("Story published to your page");
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save story");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/center/posts/${id}`);
      setPosts((prev) => prev.filter((p) => p.id !== id));
      toast.success("Story removed");
    } catch (_e) {
      toast.error("Could not remove story");
    }
  };

  return (
    <div data-testid="center-posts-panel">
      <form onSubmit={handleSubmit} className="border border-[#E5E0D8] bg-white p-6">
        <p className="label-eyebrow text-[#1E3A8A]">
          — {editingId ? "Edit story" : "Publish a story or news update"}
        </p>
        <div className="mt-4 space-y-5">
          <div>
            <label className="label-eyebrow text-[#555]">Title</label>
            <Input
              data-testid="center-post-title"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-[#111]"
            />
          </div>
          <div>
            <label className="label-eyebrow text-[#555]">Story</label>
            <Textarea
              data-testid="center-post-body"
              required
              rows={5}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              className="mt-2 rounded-none border border-[#E5E0D8] bg-transparent focus-visible:ring-0 focus-visible:border-[#111]"
            />
          </div>
          <div className="flex items-center gap-4">
            <label
              data-testid="center-post-image-upload"
              className="cursor-pointer rounded-none border border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white transition-colors px-5 py-2.5 label-eyebrow"
            >
              {form.image_url ? "Replace image" : "Add image (optional)"}
              <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageUpload} className="hidden" disabled={busy} />
            </label>
            {form.image_url && (
              <img src={resolveUrl(form.image_url)} alt="Story preview" className="w-16 h-16 object-cover border border-[#E5E0D8]" />
            )}
          </div>
          <div className="flex gap-3">
            <Button
              type="submit"
              data-testid="center-post-submit"
              disabled={busy}
              className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white px-8 py-3 h-auto label-eyebrow"
            >
              {busy ? "Saving…" : editingId ? "Save changes" : "Publish story"}
            </Button>
            {editingId && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditingId(null);
                  setForm(EMPTY);
                }}
                className="rounded-none border-[#555] text-[#555] px-6 py-3 h-auto label-eyebrow"
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      </form>

      {posts.length > 0 && (
        <div className="mt-8 space-y-4">
          <p className="label-eyebrow text-[#555]">— Your stories ({posts.length})</p>
          {posts.map((p) => (
            <div key={p.id} data-testid={`center-post-${p.id}`} className="border border-[#E5E0D8] bg-white p-5 flex gap-4">
              {p.image_url && (
                <img src={resolveUrl(p.image_url)} alt={p.title} className="w-20 h-20 object-cover border border-[#E5E0D8] flex-shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <h3 className="font-serif-display text-xl">{p.title}</h3>
                <p className="text-sm text-[#555] mt-1 line-clamp-2">{p.body}</p>
                <div className="mt-2 flex gap-4">
                  <button
                    type="button"
                    data-testid={`center-post-edit-${p.id}`}
                    onClick={() => {
                      setEditingId(p.id);
                      setForm({ title: p.title, body: p.body, image_url: p.image_url || "" });
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className="label-eyebrow text-xs text-[#1E3A8A] hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    data-testid={`center-post-delete-${p.id}`}
                    onClick={() => handleDelete(p.id)}
                    className="label-eyebrow text-xs text-[#D32F2F] hover:underline"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
